import React from 'react';
import { Search, CalendarCheck, Video } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import AnimateOnScroll, { StaggerContainer, StaggerItem } from '@/components/common/AnimateOnScroll';

const steps = [
  {
    icon: Search,
    titleEn: 'Find Your Teacher',
    titleAr: 'ابحث عن معلمك',
    descEn: 'Browse qualified teachers verified with Ijazah certification. Filter by specialty, language, and schedule.',
    descAr: 'تصفح المعلمين المؤهلين والمعتمدين بإجازة. فلتر حسب التخصص واللغة والجدول الزمني.',
    step: '01',
  },
  {
    icon: CalendarCheck,
    titleEn: 'Book a Session',
    titleAr: 'احجز جلسة',
    descEn: 'Choose a time that works for you, book a trial session, and start your learning journey.',
    descAr: 'اختر الوقت المناسب لك، احجز جلسة تجريبية، وابدأ رحلتك التعليمية.',
    step: '02',
  },
  {
    icon: Video,
    titleEn: 'Learn & Grow',
    titleAr: 'تعلّم وتقدّم',
    descEn: 'Join live video sessions, track your Hifz progress, and receive personalized feedback from your teacher.',
    descAr: 'انضم إلى جلسات فيديو مباشرة، تتبع تقدمك في الحفظ، واحصل على ملاحظات مخصصة من معلمك.',
    step: '03',
  },
];

const HowItWorks = () => {
  const { isRTL } = useLanguage();

  return (
    <section className="section bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container">
        <AnimateOnScroll className="text-center mb-14">
          <p className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-2">
            {isRTL ? 'كيف يعمل' : 'How It Works'}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
            {isRTL ? 'ابدأ رحلتك في ثلاث خطوات' : 'Start Your Journey in Three Steps'}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            {isRTL
              ? 'منصتنا تجعل من السهل التواصل مع معلم مؤهل والبدء في تعلم القرآن'
              : 'Our platform makes it easy to connect with a qualified teacher and begin your Quran learning'}
          </p>
        </AnimateOnScroll>

        <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {/* Connection line */}
          <div className="hidden md:block absolute top-16 start-[calc(16.67%+20px)] end-[calc(16.67%+20px)] h-px border-t-2 border-dashed border-border z-0" />

          {steps.map((step, i) => (
            <StaggerItem key={i}>
              <div className="relative text-center group">
                <div className="relative z-10 w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center group-hover:bg-primary/15 transition-colors duration-300">
                  <step.icon className="h-7 w-7 text-primary" />
                  <span className="absolute -top-2 -end-2 w-7 h-7 bg-amber-500 text-amber-950 text-xs font-bold rounded-full flex items-center justify-center shadow-sm">
                    {step.step}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {isRTL ? step.titleAr : step.titleEn}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
                  {isRTL ? step.descAr : step.descEn}
                </p>
              </div>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

export default HowItWorks;
